import { z } from 'zod';

import { finMayorOIgualInicio, finMayorOIgualInicioOpcional, ISO_DATE_RE } from '@/shared/lib';

import { ESTADOS_PROYECTO, FASES_PROYECTO } from './proyecto.entity';

/**
 * Espeja `CreateProyectoDto` / `UpdateProyectoDto` del backend:
 *  - `nombre`: 1..200 chars, trim.
 *  - `descripcion`: opcional, max 1000, `null` o '' limpian.
 *  - 4 FKs de catálogo: enteros positivos requeridos en CREATE.
 *  - `fechaInicio` ≤ `fechaFinEstimada` (regla cruzada, validada
 *    también en backend con `PROYECTO_FECHAS_INVALIDAS`).
 *  - `estado`/`fase` aceptan los enums declarados; default `PendienteInicio`.
 */

const nombre = z
  .string()
  .trim()
  .min(1, 'El nombre es obligatorio.')
  .max(200, 'Máximo 200 caracteres.');

const descripcion = z.string().trim().max(1000, 'Máximo 1000 caracteres.');

// Siglas del código oficial de incidencias (RI-SUP-<SIGLAS>-<N>-<YYYY>).
// Espeja el DTO backend: 2-10 alfanuméricos; el backend las guarda en
// MAYÚSCULAS y valida unicidad (409 PROYECTO_SIGLAS_DUPLICADAS). Vacío se
// normaliza a undefined/null en los adapters (fallback `P<id>`).
const siglas = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{2,10}$/, 'Solo letras y números, de 2 a 10 caracteres.');

const fkId = z.coerce.number().int().positive('Seleccione un valor del catálogo.');

const fechaIso = z
  .string()
  .min(1, 'La fecha es obligatoria.')
  .regex(ISO_DATE_RE, 'Formato inválido (YYYY-MM-DD).');

/**
 * Sub-schemas del árbol del proyecto (espejo del contrato backend
 * `POST /api/v1/proyectos`, `FaseCreateDto`). Las fases son dinámicas
 * (ADR-032): N entradas con `nombre` libre y `orden` 1..n.
 */
const ordenSubfase = z.coerce.number().int().min(1, 'El orden debe ser ≥ 1.');
const ordenFase = z.coerce.number().int().min(1, 'El orden debe ser ≥ 1.');

export const DIRECCION_META_VALUES = ['MayorOIgual', 'MenorOIgual'] as const;
export type DireccionMeta = (typeof DIRECCION_META_VALUES)[number];

export const TIPO_CONTROL_VALUES = ['CondicionMinima', 'IndicadorDesempeno'] as const;
export type TipoControl = (typeof TIPO_CONTROL_VALUES)[number];

const metaValorRegex = /^-?\d+(?:\.\d{1,4})?$/;

/**
 * Control inline unificado (`ControlCreateDto`, ADR-032). Condición e
 * indicador comparten array discriminado por `tipoControl`; los campos no
 * aplicables al tipo se omiten. El backend valida los requeridos por tipo.
 */
const controlSchema = z.object({
  tipoControl: z.enum(TIPO_CONTROL_VALUES),
  codigo: z.union([z.string().trim().max(50), z.null()]).optional(),
  descripcion: z.string().trim().min(1).max(1000).optional(),
  nombre: z.string().trim().min(1).max(200).optional(),
  unidadMedida: z.string().trim().min(1).max(50).optional(),
  metaValor: z.string().regex(metaValorRegex, 'Decimal con hasta 4 decimales.').optional(),
  direccionMeta: z.enum(DIRECCION_META_VALUES).optional(),
  orden: ordenSubfase,
});

const subfaseSchema = z
  .object({
    nombre: z.string().trim().min(1).max(200),
    fechaInicio: fechaIso,
    fechaFinEstimada: fechaIso,
    orden: ordenSubfase,
    controles: z.array(controlSchema).optional(),
  })
  .refine(...finMayorOIgualInicio('La fecha de fin de la subfase debe ser ≥ la de inicio.'));

const faseSchema = z.object({
  nombre: z.string().trim().min(1).max(200),
  orden: ordenFase,
  subfases: z.array(subfaseSchema),
});

const fasesArbolSchema = z.array(faseSchema);

/**
 * Sub-schemas del árbol para `PATCH /api/v1/proyectos/:id` (espejo de
 * `FaseUpdateDto`). Idénticos a los de creación salvo:
 *  - `id?` opcional en subfase/condición/indicador → el backend resuelve
 *    el diff (presente = update, ausente = create, faltante respecto a BD
 *    = softDelete).
 *  - `direccionMeta` es **requerido** en el indicador (el update DTO no lo
 *    declara opcional).
 *  - La fase admite `id?` para que el diff identifique fases existentes.
 * Las fases son dinámicas (`nombre` + `orden`), igual que en creación.
 */
const controlUpdateSchema = z.object({
  id: z.coerce.number().int().positive().optional(),
  tipoControl: z.enum(TIPO_CONTROL_VALUES),
  codigo: z.union([z.string().trim().max(50), z.null()]).optional(),
  descripcion: z.string().trim().min(1).max(1000).optional(),
  nombre: z.string().trim().min(1).max(200).optional(),
  unidadMedida: z.string().trim().min(1).max(50).optional(),
  metaValor: z.string().regex(metaValorRegex, 'Decimal con hasta 4 decimales.').optional(),
  // El update DTO exige `direccionMeta` en indicadores; el serializador del
  // wizard lo emite siempre para ese tipo.
  direccionMeta: z.enum(DIRECCION_META_VALUES).optional(),
  orden: ordenSubfase,
});

const subfaseUpdateSchema = z
  .object({
    id: z.coerce.number().int().positive().optional(),
    nombre: z.string().trim().min(1).max(200),
    fechaInicio: fechaIso,
    fechaFinEstimada: fechaIso,
    orden: ordenSubfase,
    controles: z.array(controlUpdateSchema).optional(),
  })
  .refine(...finMayorOIgualInicio('La fecha de fin de la subfase debe ser ≥ la de inicio.'));

const faseUpdateSchema = z.object({
  id: z.coerce.number().int().positive().optional(),
  nombre: z.string().trim().min(1).max(200),
  orden: ordenFase,
  subfases: z.array(subfaseUpdateSchema),
});

const fasesArbolUpdateSchema = z.array(faseUpdateSchema);

/**
 * ADR-033 — `POST /api/v1/proyectos` ahora persiste **solo la cabecera** y
 * crea el proyecto en estado `Borrador`. El backend rechaza (`400`,
 * `forbidNonWhitelisted`) cualquier `fases`/`estado`/usuario en el body. El
 * árbol y el equipo se agregan después con los endpoints incrementales y se
 * cierra el wizard con `/finalizar`.
 *
 * Espeja exactamente el `CreateProyectoDto` del backend tras ADR-033: 4
 * catálogos + nombre + descripción + rango de fechas (`fin ≥ inicio`).
 */
export const crearCabeceraProyectoSchema = z
  .object({
    nombre,
    siglas: siglas.optional(),
    descripcion: descripcion.optional(),
    fechaInicio: fechaIso,
    fechaFinEstimada: fechaIso,
    origenProyectoId: fkId,
    naturalezaFinancieraId: fkId,
    tipoInfraestructuraId: fkId,
    tipoServicioId: fkId,
  })
  .refine(...finMayorOIgualInicio());

export const crearProyectoSchema = z
  .object({
    nombre,
    siglas: siglas.optional(),
    descripcion: descripcion.optional(),
    estado: z.enum(ESTADOS_PROYECTO).optional(),
    fechaInicio: fechaIso,
    fechaFinEstimada: fechaIso,
    origenProyectoId: fkId,
    naturalezaFinancieraId: fkId,
    tipoInfraestructuraId: fkId,
    tipoServicioId: fkId,
    // Escalar legacy: no asigna en `proyecto_usuario`. Se conserva por
    // compatibilidad del contrato; el wizard usa `participantePrivadoIds`.
    participantePrivadoId: z.union([z.coerce.number().int().positive(), z.null()]).optional(),
    // Contrato backend: 1 supervisor (cardinalidad 1, opcional) + N
    // auxiliares (cardinalidad N, opcional). Antes era `supervisorIds`.
    supervisorId: z.coerce.number().int().positive().optional(),
    auxiliarIds: z.array(z.coerce.number().int().positive()).optional(),
    // Cardinalidad N (opcional): líder + auxiliares de campo mezclados; el
    // backend deriva el nivel del rol global de cada usuario y deduplica.
    participantePrivadoIds: z.array(z.coerce.number().int().positive()).optional(),
    // Cardinalidad N (opcional): solicitantes (actor externo de solo lectura).
    solicitanteIds: z.array(z.coerce.number().int().positive()).optional(),
    fases: fasesArbolSchema,
  })
  .refine(...finMayorOIgualInicio());

export const actualizarProyectoSchema = z
  .object({
    nombre: nombre.optional(),
    // `null` limpia las siglas (vuelve al fallback `P<id>`).
    siglas: z.union([siglas, z.null()]).optional(),
    descripcion: z.union([descripcion, z.null()]).optional(),
    estado: z.enum(ESTADOS_PROYECTO).optional(),
    fase: z.union([z.enum(FASES_PROYECTO), z.null()]).optional(),
    fechaInicio: fechaIso.optional(),
    fechaFinEstimada: fechaIso.optional(),
    origenProyectoId: fkId.optional(),
    naturalezaFinancieraId: fkId.optional(),
    tipoInfraestructuraId: fkId.optional(),
    tipoServicioId: fkId.optional(),
    participantePrivadoId: z.union([z.coerce.number().int().positive(), z.null()]).optional(),
    // Árbol completo (fases dinámicas) para el diff transaccional del
    // backend. Opcional: la edición de solo-cabecera puede omitirlo, pero el
    // wizard de edición siempre lo envía. Los integrantes NO van aquí (se
    // reconcilian con los endpoints de asignar/desasignar).
    fases: fasesArbolUpdateSchema.optional(),
  })
  .refine(...finMayorOIgualInicioOpcional());

export const filtrosProyectoSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().trim().optional(),
  estados: z.array(z.enum(ESTADOS_PROYECTO)).optional(),
  fase: z.enum(FASES_PROYECTO).optional(),
  tipoInfraestructuraId: z.coerce.number().int().positive().optional(),
  naturalezaFinancieraId: z.coerce.number().int().positive().optional(),
  includeInactive: z.coerce.boolean().optional(),
});

/**
 * Filtros del agregado `/proyectos/totales-por-estado`. Espeja los
 * filtros del listado **sin** `estados` (el agregado siempre devuelve
 * los 5 estados) ni `page`/`limit` (no aplica paginación).
 */
export const filtrosTotalesProyectoSchema = z.object({
  search: z.string().trim().optional(),
  fase: z.enum(FASES_PROYECTO).optional(),
  tipoInfraestructuraId: z.coerce.number().int().positive().optional(),
  naturalezaFinancieraId: z.coerce.number().int().positive().optional(),
  includeInactive: z.coerce.boolean().optional(),
});

export type CrearCabeceraProyectoInput = z.infer<typeof crearCabeceraProyectoSchema>;
export type CrearProyectoInput = z.infer<typeof crearProyectoSchema>;
export type ActualizarProyectoInput = z.infer<typeof actualizarProyectoSchema>;
export type FiltrosProyectoInput = z.infer<typeof filtrosProyectoSchema>;
export type FiltrosTotalesProyectoInput = z.infer<typeof filtrosTotalesProyectoSchema>;
